/*
 * Wishlist ("Preferiti", 2026-10-04; in the customer's account since 2026-10-07): the hearts of
 * snippets/nadine-heart.liquid, the header count (snippets/header-actions.liquid) and the Preferiti page
 * (sections/nadine-preferiti.liquid).
 * Saved in the customer metafield `nadine.preferiti` (product handles, newest first), printed by
 * snippets/nadine-wishlist-data.liquid and changed through the app proxy `/apps/preferiti` (Cloudflare Worker,
 * apps/preferiti-worker). Logged out, a heart opens the "Accedi" dialog and the product waits in localStorage
 * `nadine-preferiti-pending`; after the login it is added. The browser-only list of before (localStorage
 * `nadine-preferiti`) is merged into the account at the first login, then removed.
 * PostHog: `wishlist_toggled` (product, added, surface, colour on product pages, wishlist_size),
 * `wishlist_login_prompted` (product, surface), `wishlist_merged` (items).
 *
 * Also "Avvisami quando torna" (2026-10-13, Vitto: login required, to know who asked): the product page's buttons
 * `[data-nadine-notify="<variant id>"]` (blocks/nadine-in-store.liquid, which keeps the variant and its colour/size
 * up to date and sends `nadine:notify-sync`) toggle the variant in the customer metafield `nadine.avvisami` through
 * the same app proxy; the button shows the state with aria-pressed. Logged out: the same dialog with the notify
 * texts, the variant waits in localStorage `nadine-avvisami-pending` and is saved after the login.
 * PostHog: `back_in_stock_toggled` (variant, colour in Italian, size, added, surface, requests, after_login),
 * `back_in_stock_login_prompted` (variant, colour, size, surface); `product` comes from the page context.
 */
/**
 * @typedef {{ handle: string, title: string, price: string, url: string, image?: string, srcset?: string, subtitle?: string }} PreferitiProduct
 * @typedef {HTMLElement & { _products?: Record<string, PreferitiProduct> }} PreferitiRoot
 */
(() => {
  const LEGACY = 'nadine-preferiti';
  const PENDING = 'nadine-preferiti-pending';
  const CACHE = 'nadine-preferiti-account';
  const NOTIFY_PENDING = 'nadine-avvisami-pending';
  // A list written in the last two minutes beats the one printed by Liquid (the page may predate the write)
  const CACHE_MS = 120000;

  /** @type {{ customer: string | null, handles?: unknown, notify?: unknown, proxy: string, login: string, i18n: Record<string, string> }} */
  let data;
  try {
    data = JSON.parse(document.getElementById('nadine-wishlist-data')?.textContent ?? '');
  } catch {
    data = { customer: null, handles: [], notify: [], proxy: '/apps/preferiti', login: '/customer_authentication/login', i18n: {} };
  }
  const customer = data.customer;

  const storage = {
    /** @param {string} key */
    get(key) {
      try {
        return JSON.parse(localStorage.getItem(key) || 'null');
      } catch {
        return null;
      }
    },
    /** @param {string} key @param {unknown} value */
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* private mode or storage blocked */
      }
    },
    /** @param {string} key */
    remove(key) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* storage blocked */
      }
    },
  };

  /** @param {unknown} list @returns {string[]} */
  const handlesOnly = (list) => (Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : []);

  const fromCache = () => {
    const cached = storage.get(CACHE);
    if (customer && cached && cached.customer === customer && Date.now() - cached.at < CACHE_MS) return handlesOnly(cached.handles);
    return null;
  };

  let saved = customer ? fromCache() || handlesOnly(data.handles) : [];
  // "Avvisami quando torna": variant IDs, newest first
  let requested = customer ? handlesOnly(data.notify) : [];

  /** @param {string[]} list */
  const remember = (list) => {
    saved = list;
    storage.set(CACHE, { customer, handles: list, at: Date.now() });
  };

  const sync = () => {
    document.querySelectorAll('[data-nadine-heart]').forEach((element) => {
      const button = /** @type {HTMLElement} */ (element);
      const on = saved.includes(button.dataset.nadineHeart ?? '');
      button.setAttribute('aria-pressed', String(on));
      const label = on ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-nadine-wishlist-count]').forEach((element) => {
      const badge = /** @type {HTMLElement} */ (element);
      badge.textContent = saved.length ? String(saved.length) : '';
      badge.hidden = !saved.length;
    });
    document.querySelectorAll('[data-nadine-preferiti]').forEach((root) => renderPage(/** @type {PreferitiRoot} */ (root)));
  };

  const syncNotify = () => {
    document.querySelectorAll('[data-nadine-notify]').forEach((element) => {
      const button = /** @type {HTMLElement} */ (element);
      button.setAttribute('aria-pressed', String(requested.includes(button.dataset.nadineNotify ?? '')));
    });
  };
  // The product page's block changed the variant on its buttons
  document.addEventListener('nadine:notify-sync', syncNotify);

  /* Requests to the app proxy, one at a time so the account sees the clicks in order */
  /** @type {Promise<unknown>} */
  let queue = Promise.resolve();
  /** @param {Record<string, unknown>} body @param {string} [field] the list in the reply @returns {Promise<string[]>} */
  const post = (body, field = 'handles') => {
    const run = queue.then(async () => {
      const response = await fetch(data.proxy, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json', 'x-nadine-preferiti': '1' },
        body: JSON.stringify(body),
        credentials: 'same-origin',
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(result[field])) {
        const error = /** @type {Error & { status?: number }} */ (new Error(result.error || String(response.status)));
        error.status = response.status;
        throw error;
      }
      return handlesOnly(result[field]);
    });
    queue = run.catch(() => {});
    return run;
  };

  const loginUrl = () => `${data.login}&return_to=${encodeURIComponent(location.pathname + location.search)}`;

  /** @type {HTMLDialogElement | undefined} */
  let dialog;
  const HEART =
    '<path d="M12 20.5s-7.5-4.6-9.3-9.4C1.5 7.9 3.6 4.5 7 4.5c2.1 0 3.6 1.2 5 3 1.4-1.8 2.9-3 5-3 3.4 0 5.5 3.4 4.3 6.6-1.8 4.8-9.3 9.4-9.3 9.4Z"/>';
  const BELL = '<path d="M18 16v-5a6 6 0 0 0-12 0v5l-1.5 2h15L18 16Z"/><path d="M10 20.5a2 2 0 0 0 4 0"/>';
  /** @param {'wishlist' | 'notify'} [kind] the heart, or "Avvisami quando torna" */
  const openLogin = (kind = 'wishlist') => {
    const t = data.i18n || {};
    if (!dialog) {
      const created = document.createElement('dialog');
      dialog = created;
      dialog.className = 'nadine-wish-login';
      dialog.setAttribute('aria-labelledby', 'nadine-wish-login-title');
      dialog.innerHTML = `
        <button type="button" class="nadine-wish-login__close" aria-label="${escape(t.close)}">&times;</button>
        <svg class="nadine-wish-login__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"></svg>
        <h2 class="nadine-wish-login__title" id="nadine-wish-login-title"></h2>
        <p class="nadine-wish-login__text"></p>
        <a class="button nadine-wish-login__button" data-nadine-wish-login-link autofocus>${escape(t.button)}</a>`;
      dialog.querySelector('.nadine-wish-login__close')?.addEventListener('click', () => created.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === created) created.close(); // the backdrop
      });
      document.body.append(dialog);
    }
    const notify = kind === 'notify';
    const icon = dialog.querySelector('.nadine-wish-login__icon');
    if (icon) {
      icon.innerHTML = notify ? BELL : HEART;
      icon.classList.toggle('nadine-wish-login__icon--notify', notify);
    }
    const title = dialog.querySelector('.nadine-wish-login__title');
    if (title) title.textContent = (notify ? t.notify_title : t.title) ?? '';
    const text = dialog.querySelector('.nadine-wish-login__text');
    if (text) text.textContent = (notify ? t.notify_text : t.text) ?? '';
    /** @type {HTMLAnchorElement | null} */
    const link = dialog.querySelector('[data-nadine-wish-login-link]');
    if (link) link.href = loginUrl();
    if (!dialog.open) dialog.showModal();
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let toastTimer;
  /** @param {string} text */
  const toast = (text) => {
    /** @type {HTMLElement | null} */
    let found = document.querySelector('.nadine-wish-toast');
    if (!found) {
      found = document.createElement('div');
      found.className = 'nadine-wish-toast';
      found.setAttribute('role', 'status');
      document.body.append(found);
    }
    const node = found;
    node.textContent = text;
    node.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (node.hidden = true), 4000);
  };

  /** @param {HTMLElement} button */
  const surfaceOf = (button) => {
    if (button.closest('[data-nadine-preferiti]')) return 'wishlist_page';
    if (button.closest('.nadine-row')) return 'product_row';
    if (button.closest('.card-gallery')) return 'collection_card';
    if (button.closest('.nadine-abiti__track')) return 'landing_card';
    if (button.classList.contains('nadine-heart--gallery')) return 'product_gallery';
    return 'product_page'; // beside the WhatsApp button
  };

  // Product page hearts: the colour on show, in Italian in every language (as product_viewed and contact_clicked)
  /** @param {HTMLElement} button */
  const colourOf = (button) => {
    if (button.closest('.nadine-row, .card-gallery, .nadine-abiti__track, [data-nadine-preferiti]')) return undefined;
    // The colour is the first option: buttons, or text when the product has one colour (data-nadine-single)
    /** @type {HTMLElement | null} */
    const first = document.querySelector('variant-picker fieldset, variant-picker [data-nadine-single]');
    if (!first) return undefined;
    if (first.matches('[data-nadine-single]')) return first.dataset.nadineColourIt || first.dataset.value;
    /** @type {HTMLInputElement | null} */
    const input = first.querySelector('input:checked');
    return input ? input.dataset.nadineColourIt || input.value : undefined;
  };

  /** @param {HTMLElement} button */
  const pop = (button) => {
    button.classList.remove('is-popping');
    void button.offsetWidth;
    button.classList.add('is-popping');
  };

  // Capture phase: the card around a heart (Horizon's product card) must not see the click and navigate.
  document.addEventListener(
    'click',
    (event) => {
      /** @type {HTMLElement | null} */
      const button = event.target instanceof Element ? event.target.closest('[data-nadine-heart]') : null;
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();

      const handle = button.dataset.nadineHeart;
      if (!handle) return;
      const surface = surfaceOf(button);
      const colour = colourOf(button);

      if (!customer) {
        storage.set(PENDING, { handle, surface, colour, at: Date.now() });
        window.posthog?.capture?.('wishlist_login_prompted', { product: handle, surface });
        openLogin();
        return;
      }

      const before = saved;
      const added = !saved.includes(handle);
      saved = added ? [handle, ...saved] : saved.filter((h) => h !== handle);
      if (added) pop(button);
      else button.classList.remove('is-popping');
      sync();
      window.posthog?.capture?.('wishlist_toggled', { product: handle, added, surface, colour, wishlist_size: saved.length });

      post({ action: added ? 'add' : 'remove', handle })
        .then((list) => {
          remember(list);
          sync();
        })
        .catch((error) => {
          saved = before;
          sync();
          if (error.status === 401) openLogin();
          else toast(data.i18n?.error || 'Error');
        });
    },
    true
  );

  /* "Avvisami quando torna" (blocks/nadine-in-store.liquid): the colour, size and variant come from the button */
  document.addEventListener('click', (event) => {
    /** @type {HTMLElement | null} */
    const button = event.target instanceof Element ? event.target.closest('[data-nadine-notify]') : null;
    if (!button) return;
    event.preventDefault();
    const variant = button.dataset.nadineNotify;
    if (!variant) return;
    const details = {
      variant,
      colour: button.dataset.colour || undefined,
      size: button.dataset.size || undefined,
      surface: button.dataset.surface || 'product_page',
    };

    if (!customer) {
      storage.set(NOTIFY_PENDING, { ...details, at: Date.now() });
      window.posthog?.capture?.('back_in_stock_login_prompted', details);
      openLogin('notify');
      return;
    }

    const before = requested;
    const added = !requested.includes(variant);
    requested = added ? [variant, ...requested] : requested.filter((v) => v !== variant);
    syncNotify();
    window.posthog?.capture?.('back_in_stock_toggled', { ...details, added, requests: requested.length });

    post({ list: 'avvisami', action: added ? 'add' : 'remove', variant }, 'variants')
      .then((list) => {
        requested = list;
        syncNotify();
      })
      .catch((error) => {
        requested = before;
        syncNotify();
        if (error.status === 401) openLogin('notify');
        else toast(data.i18n?.error || 'Error');
      });
  });

  /* After the login: the "Avvisami" asked for before it (less than a day old) */
  const notifyAfterLogin = async () => {
    const pending = storage.get(NOTIFY_PENDING);
    if (!pending) return;
    storage.remove(NOTIFY_PENDING);
    if (!pending.variant || !(Date.now() - pending.at < 86400000)) return;
    try {
      if (!requested.includes(pending.variant)) {
        requested = await post({ list: 'avvisami', action: 'add', variant: pending.variant }, 'variants');
        syncNotify();
        window.posthog?.capture?.('back_in_stock_toggled', {
          variant: pending.variant,
          colour: pending.colour,
          size: pending.size,
          surface: pending.surface,
          added: true,
          after_login: true,
          requests: requested.length,
        });
      }
      // Back from the login page: say it worked, the button alone is easy to miss
      toast(data.i18n?.notify_saved || '');
    } catch {
      /* the visitor can tap the button again */
    }
  };

  /* After the login: the heart clicked before it (pending, less than a day old), then the old browser-only list */
  const afterLogin = async () => {
    const pending = storage.get(PENDING);
    const legacy = handlesOnly(storage.get(LEGACY));
    if (pending) {
      storage.remove(PENDING);
      if (pending.handle && Date.now() - pending.at < 86400000 && !saved.includes(pending.handle)) {
        try {
          remember(await post({ action: 'add', handle: pending.handle }));
          sync();
          window.posthog?.capture?.('wishlist_toggled', {
            product: pending.handle,
            added: true,
            surface: pending.surface,
            colour: pending.colour,
            after_login: true,
            wishlist_size: saved.length,
          });
        } catch {
          /* the visitor can tap the heart again */
        }
      }
    }
    if (legacy.length) {
      try {
        remember(await post({ action: 'merge', handles: legacy }));
        storage.remove(LEGACY);
        sync();
        window.posthog?.capture?.('wishlist_merged', { items: legacy.length, wishlist_size: saved.length });
      } catch {
        /* kept in the browser: tried again on the next page */
      }
    }
  };

  // Another tab changed the list
  window.addEventListener('storage', (event) => {
    if (event.key !== CACHE) return;
    const list = fromCache();
    if (!list) return;
    saved = list;
    sync();
  });

  // Back/forward cache: the page may come back with an old list
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const list = fromCache();
    if (list) saved = list;
    sync();
  });

  /** @param {unknown} value */
  const escape = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (c) => /** @type {Record<string, string>} */ ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c
    );

  /* Preferiti page: the section prints every catalog product as JSON; show the saved ones, newest first */
  /** @param {PreferitiRoot} root */
  function renderPage(root) {
    if (!root._products) {
      try {
        root._products = JSON.parse(root.querySelector('script[type="application/json"]')?.textContent ?? '');
      } catch {
        root._products = {};
      }
    }
    const products = root._products ?? {};
    /** @type {PreferitiProduct[]} */
    const items = saved.flatMap((handle) => products[handle] ?? []);
    /** @type {HTMLElement | null} */
    const list = root.querySelector('[data-nadine-preferiti-list]');
    /** @type {HTMLElement | null} */
    const empty = root.querySelector('[data-nadine-preferiti-empty]');
    /** @type {HTMLElement | null} */
    const count = root.querySelector('[data-nadine-preferiti-count]');
    /** @type {HTMLAnchorElement | null} */
    const send = root.querySelector('[data-nadine-preferiti-send]');
    /** @type {HTMLElement | null} */
    const login = root.querySelector('[data-nadine-preferiti-login]');
    if (!list || !empty) return;

    if (login) {
      login.hidden = Boolean(customer);
      const link = login.querySelector('a');
      if (link) link.href = loginUrl();
    }
    empty.hidden = !customer || items.length > 0;
    list.hidden = items.length === 0;
    if (count) {
      const n = count.querySelector('[data-nadine-preferiti-n]');
      if (n) {
        n.textContent =
          items.length === 1 ? root.dataset.countOne ?? '' : (root.dataset.countOther ?? '').replace('%n', String(items.length));
      }
      count.hidden = items.length === 0;
    }
    // "Invia la lista su WhatsApp": one line per saved item, name, price and link (cover colour's variant)
    if (send) {
      const lines = items.map((p) => `- ${p.title} (${p.price}) ${new URL(p.url, location.origin).href}`);
      const text = [send.dataset.waText, ...lines].join('\n');
      send.href = `https://wa.me/${send.dataset.nadinePreferitiSend}?text=${encodeURIComponent(text)}`;
      send.dataset.trackItems = String(items.length);
      send.dataset.trackProducts = items.map((p) => p.handle).join(',');
      send.hidden = items.length === 0;
    }

    const key = items.map((p) => p.handle).join(',');
    if (list.dataset.rendered === key) return;
    list.dataset.rendered = key;
    list.innerHTML = items
      .map(
        (p) => `<li class="nadine-preferiti__item">
          <a class="nadine-preferiti__link" href="${escape(p.url)}">
            <span class="nadine-preferiti__media">${
              p.image
                ? `<img src="${escape(p.image)}" srcset="${escape(p.srcset)}" sizes="(min-width: 990px) 25vw, 50vw" width="1050" height="1500" alt="${escape(p.title)}" loading="lazy">`
                : ''
            }</span>
            <span class="nadine-preferiti__name">${escape(p.title)}</span>
            ${p.subtitle ? `<span class="nadine-card-subtitle">${escape(p.subtitle)}</span>` : ''}
            <span class="nadine-preferiti__price">${escape(p.price)}</span>
          </a>
          <button type="button" class="nadine-heart nadine-heart--card" data-nadine-heart="${escape(p.handle)}"
            aria-pressed="true" aria-label="${escape((root.dataset.labelRemove ?? '').replace('%product', p.title))}"
            data-label-add="${escape((root.dataset.labelAdd ?? '').replace('%product', p.title))}"
            data-label-remove="${escape((root.dataset.labelRemove ?? '').replace('%product', p.title))}">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.3-9.4C1.5 7.9 3.6 4.5 7 4.5c2.1 0 3.6 1.2 5 3 1.4-1.8 2.9-3 5-3 3.4 0 5.5 3.4 4.3 6.6-1.8 4.8-9.3 9.4-9.3 9.4Z"/></svg>
          </button>
        </li>`
      )
      .join('');
  }

  const start = () => {
    sync();
    syncNotify();
    if (customer) {
      afterLogin();
      notifyAfterLogin();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
